import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	ArrayMinSize,
	IsArray,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	Matches,
	MaxLength,
	Min,
	ValidateNested,
} from 'class-validator';

/**
 * PD-16251 — the most item cost changes one request may report. The ERP
 * integration sends them in chunks well below this; the cap is a sanity bound
 * on a single queue job.
 */
export const ITEM_COST_CHANGES_MAX_PER_REQUEST = 1000;

/**
 * One item whose cost the ERP changed (PD-16251), as reported by the ERP
 * integration after it wrote the new cost to the item.
 *
 * `changeId` is generated by the integration when it detects the change and is
 * kept when a failed report is retried, so a change reported twice is applied
 * once (crm-api records every change it processed).
 */
export class ItemCostChangeDto {
	// A UUID from the integration; the pattern keeps it to the characters a
	// UUID (or any other opaque token) uses.
	@IsString()
	@MaxLength(64)
	@Matches(/^[A-Za-z0-9-]+$/)
	public changeId!: string;

	/** The Workd item id (one ERP item can map to several Workd items). */
	@IsInt()
	@Min(1)
	public itemId!: number;

	/** The ERP's item id, for logs only. */
	@IsOptional()
	@IsString()
	@MaxLength(255)
	public externalId?: string | null;

	@IsNumber({ allowNaN: false, allowInfinity: false })
	@Min(0)
	public previousCost!: number;

	@IsNumber({ allowNaN: false, allowInfinity: false })
	@Min(0)
	public newCost!: number;
}

/** Body of `POST /api/integrations/item-cost-changes` (PD-16251). */
export class SubmitItemCostChangesDto {
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(ITEM_COST_CHANGES_MAX_PER_REQUEST)
	@ValidateNested({ each: true })
	@Type(() => ItemCostChangeDto)
	public changes!: Array<ItemCostChangeDto>;
}

/** Response of `POST /api/integrations/item-cost-changes` (PD-16251). */
export interface SubmitItemCostChangesResponseModel {
	/** How many changes were queued. */
	accepted: number;
	jobId: string;
}

/**
 * Response of `GET /api/integrations/item-cost-changes/status` (PD-16251):
 * whether the tenant's ERP integration has ever reported item costs.
 */
export interface ItemCostReportingStatusResponseModel {
	/** ISO time of the last report, or null when none has arrived. */
	lastReportedAt: string | null;
}
